<template>

  <div>
    <b-breadcrumb>
      <b-breadcrumb-item>Administratie</b-breadcrumb-item>
      <b-breadcrumb-item to="/main/admin/contracts">Contracten</b-breadcrumb-item>
      <b-breadcrumb-item active>Prijsindexatie</b-breadcrumb-item>
    </b-breadcrumb>

    <div class="d-flex align-items-center justify-content-between">
      <h2>Prijsindexatie</h2>
      <b-btn variant="primary" @click="execute" :disabled="busy"><span class="mdi mdi-ticket-percent-outline" /> Indexatie uitvoeren</b-btn>
    </div>

    <b-container fluid>
      <b-row>
        <b-col>
          <table class="table table-sm table-bordered" style="table-layout: fixed">
            <thead>
              <tr class="text-center">
                <th></th>
                <th colspan="2">In forfait</th>
                <th colspan="2">Supplementair</th>
                <th>Status</th>
              </tr>
              <tr>
                <th>Tuple</th>
                <th>Voor indexatie</th>
                <th>Na indexatie</th>
                <th>Voor indexatie</th>
                <th>Na indexatie</th>
                <th></th>
              </tr>
              <tr>
                <th></th>
                <th colspan="2">
                  <b-input-group prepend="Verhogen met" append="%">
                    <b-input type="number" min="0" max="100" step="0.01" v-model="increaseForfait" />
                  </b-input-group>
                </th>
                <th colspan="2">
                  <b-input-group prepend="Verhogen met" append="%">
                    <b-input type="number" min="0" max="100" step="0.01" v-model="increaseSuppl" />
                  </b-input-group>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
            <template v-for="contract in contracts.nodes">
              <tr :class="{'bg-secondary': !contract.indexation}">
                <td colspan="6">
                    <span>Contract <strong>{{contract.name}}</strong></span> |
                    <span v-if="!contract.indexation" class="text-warning">Indexatie uitgeschakeld voor dit contract</span>
                    <span v-else>Laatst geïndexeerd: {{contract.lastIndexation === null ? 'Nooit' : contract.lastIndexation|format_duration($store.state.now)}}</span> 
                    | <span>
                      Renting: <span v-if="contract.renting">ja</span> <span v-else>nee</span>
                    </span>
                </td>
              </tr>
              <tr v-for="tuple in contract.tuples.nodes"  :class="{'bg-secondary': !contract.indexation}" class="text-right">
                <td>{{tuple.cgroup.displayName}}</td>

                <td :class="{'bg-secondary text-secondary': contract.renting}">€ {{parseFloat(tuple.forfaitPrice).toFixed(4)}}</td>
                <td :class="{'bg-secondary text-secondary': contract.renting}" v-if="contract.indexation && !contract.renting">{{tuple.forfaitPrice|format_indexation(increaseForfait)}}</td>
                <td :class="{'bg-secondary text-secondary': contract.renting}" v-else>€ {{parseFloat(tuple.forfaitPrice).toFixed(4)}}</td>

                <td>€ {{parseFloat(tuple.supplPricePerUnit).toFixed(4)}}</td>
                <td v-if="contract.indexation">{{tuple.supplPricePerUnit|format_indexation(increaseSuppl)}}</td>
                <td v-else>€ {{parseFloat(tuple.supplPricePerUnit).toFixed(4)}}</td>
                
                <td>
                  <span v-if="status[contract.contractId] === 1" class="text-primary">Bezig...</span>
                  <span v-else-if="status[contract.contractId] === -1" class="text-danger">Fout</span>
                  <span v-else-if="status[contract.contractId] === 2" class="text-success">Bijgewerkt</span>
                  <span v-else-if="status[contract.contractId] === 3" class="text-info">Overgeslaan</span>
                  <span v-else>Ongewijzigd</span>
                </td>
              </tr>
            </template>
            </tbody>
          </table>
        </b-col>
      </b-row>
    </b-container>

  </div>

</template>

<script>
import {Decimal} from 'decimal.js';

import {NETWORK_ONLY} from '@/lib/constants';

export default
{
    apollo:
    {
        contracts:
        {
            fetchPolicy: NETWORK_ONLY,
            query: require('../../graphql/query/contracts_for_indexation.gql'),
            variables() { return { contractIds: this.contractIds } }
        }
    },
    async mounted()
    {
        const { data: { cgroups } } = await this.$apollo.query(
            {
                fetchPolicy: NETWORK_ONLY,
                query: require('../../graphql/query/cgroups_for_indexation.gql'),
                variables: { contractIds: this.contractIds }
            }
        );

        this.cgroups = cgroups;
    },
    data()
    {
        return {
            busy: false,
            cgroups: { nodes: [] },
            contracts: { nodes: [] },
            increaseForfait: 0,
            increaseSuppl: 0,
            status: { }
        }
    },
    methods:
    {
        async execute()
        {
            const yn = await this.$bvModal.msgBoxConfirm(
                `U staat op het punt ${this.contractIds.length} contracten aan te passen. Doorgaan?`,
                {
                    centered: true,
                    okTitle: 'Indexatie uitvoeren',
                    cancelTitle: 'Annuleren',
                    title: '2 keer nadenken'
                }
            );
            
            if(yn !== true)
                return;

            this.busy = true;

            this.contractIds.forEach(id => this.$set(this.status, id, 1));

            const lastIndexation = Date.now();
            
            let contract, contractId, tuples;
            
            for(contractId of this.contractIds)
            {
                contract = (await this.$apollo.query(
                    {
                        query: require('../../graphql/query/contract_for_indexation.gql'),
                        variables: { contractId },
                        fetchPolicy: NETWORK_ONLY
                    }
                )).data.contract;

                if(!contract.indexation)
                {
                    this.$set(this.status, contract.contractId, 3);
                    continue;
                }

                tuples = contract.tuples.nodes.map(tuple =>
                    {
                        const patch = {
                            supplPricePerUnit: new Decimal(tuple.supplPricePerUnit).mul(1 + parseFloat(this.increaseSuppl) / 100).toFixed(4)
                        };
                        
                        if(!contract.renting)
                            patch.forfaitPrice = new Decimal(tuple.forfaitPrice).mul(1 + parseFloat(this.increaseForfait) / 100).toFixed(4)
                        
                        return { id: tuple.id, patch };
                    }
                );

                try
                {
                    await this.$apollo.mutate(
                        {
                            mutation: require('../../graphql/mutation/contract_price_indexation.gql'),
                            variables: { contractId, lastIndexation, tuples }
                        }
                    );
                    
                    this.$set(this.status, contractId, 2);
                }
                catch (ex)
                {
                    console.error(ex);
                    this.$set(this.status, contractId, -1);
                }
            }
        }
    },
    props:
    {
        contractIds: { type: Array, required: true }
    }
}
</script>

<style>
</style>